/**
 * Phase 6 Vendor-Port Wave 1 — SHARED RATED-OUTPUT KIT (§33 boilerplate intro).
 *
 * <RatedOutputsTable> is the GENERIC sortable result table that the §33 spec
 * calls out as common across EVERY vendor selector:
 *
 *   '"rated outputs" tmpl rendered into `#for-output-rendering`-style containers
 *    with sortable result tables, generation timestamp (`generetionTimeStamp`
 *    [sic]), warning/error blocks; required-field asterisk engine …'
 *
 * The legacy per-selector "rated outputs" tmpls (coil 3,065 lines, EW, PHX,
 * gas_heater, fan, uvc) all share the SAME shape:
 *   - a column header row whose cells are click-to-sort (the `sort_model` helper
 *     toggles asc/desc by `data-key`);
 *   - N body rows (one per rated model / scheduled part);
 *   - per-cell number formatting (decimal toFixed(2)+commas / integer rounding);
 *   - a "Rated on : <timestamp>" line (the `.rating-time` block; the legacy
 *     variable is misspelled `generetionTimeStamp` — the RENDERED label is just
 *     "Rated on : <datetime>", which this component matches);
 *   - warning / error / notes blocks above or below the table.
 *
 * This is intentionally PRESENTATIONAL + GENERIC: columns + rows are props, so
 * Waves 2-6 (coil / energy_wheel / phx / gas_heater / fan) drive their own
 * column sets and row data through the same component. UVC's rated-output sheet
 * is itself a multi-section parts table (scheduled / all-parts / electrical /
 * notes) — it composes this kit's <RatedSortableTable> per section rather than
 * being one flat table; the kit therefore exports BOTH the high-level
 * <RatedOutputsTable> (single-table case) and the lower-level pieces
 * (<RatedSortableTable>, <RatedNotesBlock>, <RatedMessageBlock>, RatedOn,
 * useRatedSort, formatRatedCell) for selectors whose sheets are multi-block.
 *
 * SORT (legacy sort_model): clicking a sortable column header sorts the rows by
 * that column's `key` ascending; clicking the same header again flips to
 * descending. A 3rd state (no-sort, original order) is NOT in legacy — it stays
 * asc/desc toggling. Numeric columns sort numerically; others lexically.
 */

import { useCallback, useMemo, useState } from 'react';
import { formatDecimalDisplay, formatIntegerDisplay } from '../../formUtils';

// ---------------------------------------------------------------------------
// types
// ---------------------------------------------------------------------------

/** A column definition for the sortable rated table. */
export interface RatedColumn<R = Record<string, unknown>> {
  /** the row key this column reads (also the sort `data-key`). */
  key: keyof R & string;
  /** header label (verbatim from the legacy thead cell). */
  label: React.ReactNode;
  /** cell number formatting (legacy per-cell formatter_type). */
  format?: 'decimal' | 'integer' | 'text';
  /** whether the header is click-to-sort (legacy sortable cells carry data-key
   * + the sort handler; static cells like MODEL/DESCRIPTION are not sortable). */
  sortable?: boolean;
  /** sort comparison hint — 'number' sorts numerically (default inferred from
   * format), 'string' forces lexical. */
  sortType?: 'number' | 'string';
  /** bootstrap col-xs-N width (legacy grid) — rendered as the cell className. */
  colClass?: string;
  /** text alignment (legacy text-right etc.). */
  align?: 'left' | 'right' | 'center';
  /** optional custom cell renderer (overrides format-based rendering). */
  render?: (row: R, value: unknown) => React.ReactNode;
}

export type SortDir = 'asc' | 'desc';

export interface RatedSortState {
  key: string | null;
  dir: SortDir;
}

// ---------------------------------------------------------------------------
// cell formatting (legacy validateOutputs / number_output rules)
// ---------------------------------------------------------------------------

/** Format a single rated cell value per the legacy number_output rules:
 *  decimal → toFixed(2) + comma separator; integer → rounded + comma; else as-is. */
export function formatRatedCell(
  value: unknown,
  format: RatedColumn['format'] = 'text'
): string {
  if (value == null || value === '') return '';
  if (format === 'decimal') return formatDecimalDisplay(value);
  if (format === 'integer') return formatIntegerDisplay(value);
  return String(value);
}

const alignClass = (align?: RatedColumn['align']) =>
  align === 'right' ? ' text-right' : align === 'center' ? ' text-center' : '';

// ---------------------------------------------------------------------------
// useRatedSort — the legacy sort_model state machine (asc → desc toggle)
// ---------------------------------------------------------------------------

export function useRatedSort<R extends Record<string, unknown>>(
  rows: R[],
  columns: RatedColumn<R>[]
): { sorted: R[]; sort: RatedSortState; onSort: (key: string) => void } {
  const [sort, setSort] = useState<RatedSortState>({ key: null, dir: 'asc' });

  const onSort = useCallback((key: string) => {
    setSort((prev) =>
      prev.key === key
        ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
        : { key, dir: 'asc' }
    );
  }, []);

  const sorted = useMemo(() => {
    if (!sort.key) return rows;
    const col = columns.find((c) => c.key === sort.key);
    if (!col) return rows;
    const numeric =
      col.sortType === 'number' ||
      (col.sortType == null && (col.format === 'decimal' || col.format === 'integer'));
    const out = [...rows];
    out.sort((a, b) => {
      const av = a[sort.key as keyof R];
      const bv = b[sort.key as keyof R];
      let cmp: number;
      if (numeric) {
        const an = Number(av);
        const bn = Number(bv);
        const aNaN = Number.isNaN(an);
        const bNaN = Number.isNaN(bn);
        if (aNaN && bNaN) cmp = 0;
        else if (aNaN) cmp = 1; // nulls/NaN sink to the bottom (legacy behaviour)
        else if (bNaN) cmp = -1;
        else cmp = an - bn;
      } else {
        cmp = String(av ?? '').localeCompare(String(bv ?? ''));
      }
      return sort.dir === 'asc' ? cmp : -cmp;
    });
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, sort, columns]);

  return { sorted, sort, onSort };
}

// ---------------------------------------------------------------------------
// RatedOn — the "Rated on : <timestamp>" line (legacy generetionTimeStamp [sic])
// ---------------------------------------------------------------------------

export const RatedOn = ({ datetime }: { datetime?: string | null }) =>
  datetime ? (
    <div className="row rating-time">
      <span>Rated on : {datetime}</span>
    </div>
  ) : null;

// ---------------------------------------------------------------------------
// RatedMessageBlock — warning / error block (legacy .error-message / .warning)
// ---------------------------------------------------------------------------

export const RatedMessageBlock = ({
  errors,
  warnings,
}: {
  errors?: string[];
  warnings?: string[];
}) => {
  const errs = errors?.filter(Boolean) ?? [];
  const warns = warnings?.filter(Boolean) ?? [];
  if (!errs.length && !warns.length) return null;
  return (
    <>
      {errs.map((e, i) => (
        <div className="error-message" key={`e${i}`}>
          {e}
        </div>
      ))}
      {warns.map((w, i) => (
        <div className="warning-message" key={`w${i}`}>
          {w}
        </div>
      ))}
    </>
  );
};

// ---------------------------------------------------------------------------
// RatedNotesBlock — Mfg's Notes list (legacy rated_notes loop)
// ---------------------------------------------------------------------------

export const RatedNotesBlock = ({
  notes,
  label = "Mfg's Notes : ",
}: {
  notes: Array<{ note: string } | string>;
  label?: string;
}) => (
  <div className="clearfix row nots_and_errors">
    <div className="col-xs-1">
      <p className="label">{label}</p>
    </div>
    <div className="col-xs-11">
      {notes.map((n, i) => (
        <p className="label" key={i}>
          {typeof n === 'string' ? n : n.note}
        </p>
      ))}
    </div>
  </div>
);

// ---------------------------------------------------------------------------
// RatedSortableTable — the lower-level sortable table (thead + tbody)
// ---------------------------------------------------------------------------

export interface RatedSortableTableProps<R extends Record<string, unknown>> {
  columns: RatedColumn<R>[];
  rows: R[];
  /** stable row key extractor (defaults to index). */
  rowKey?: (row: R, index: number) => string | number;
  /** extra className on each body row (legacy scheduled_parts / all_parts). */
  rowClass?: (row: R, index: number) => string | undefined;
  /** body-row click (legacy `click .fan_rated_item → chooseFan`). NOTE the
   * index is the DISPLAY index (post-sort) — callers holding a source array
   * should locate the row by identity. */
  onRowClick?: (row: R, index: number) => void;
  /** disable internal sort (when the caller pre-sorts). */
  disableSort?: boolean;
}

export function RatedSortableTable<R extends Record<string, unknown>>({
  columns,
  rows,
  rowKey,
  rowClass,
  onRowClick,
  disableSort,
}: RatedSortableTableProps<R>) {
  const { sorted, sort, onSort } = useRatedSort(rows, columns);
  const display = disableSort ? rows : sorted;

  return (
    <div className="rated-sortable-table full-height">
      <div className="col-xs-12 thead-part">
        <div className="row">
          {columns.map((c) => {
            const isSorted = sort.key === c.key;
            return (
              <div
                key={c.key}
                className={`${c.colClass ?? ''}${alignClass(c.align)}${
                  c.sortable ? ' sortable' : ''
                }${isSorted ? ` sorted sorted-${sort.dir}` : ''}`}
                data-key={c.sortable ? c.key : undefined}
                onClick={c.sortable ? () => onSort(c.key) : undefined}
                role={c.sortable ? 'button' : undefined}
              >
                {c.label}
                {c.sortable && isSorted && (
                  <i className={`sort-arrow sort-${sort.dir}`} />
                )}
              </div>
            );
          })}
        </div>
      </div>
      <div className="col-xs-12 tbody-part">
        {display.map((row, idx) => (
          <div
            className={`row${rowClass ? ` ${rowClass(row, idx) ?? ''}` : ''}`}
            key={rowKey ? rowKey(row, idx) : idx}
            onClick={onRowClick ? () => onRowClick(row, idx) : undefined}
          >
            {columns.map((c) => {
              const value = row[c.key];
              return (
                <div
                  key={c.key}
                  className={`${c.colClass ?? ''}${alignClass(c.align)} top-padding`}
                >
                  {c.render ? (
                    c.render(row, value)
                  ) : (
                    <span>{formatRatedCell(value, c.format)}</span>
                  )}
                </div>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// RatedOutputsTable — the high-level single-table rated-output sheet
// ---------------------------------------------------------------------------

export interface RatedOutputsTableProps<R extends Record<string, unknown>> {
  /** column set (sortable headers + per-cell formatting). */
  columns: RatedColumn<R>[];
  /** rated rows (e.g. coil's multiple rated models, fan's rated_fans). */
  rows: R[];
  /** "Rated on : <datetime>" timestamp (legacy generetionTimeStamp [sic]). */
  ratedDatetime?: string | null;
  /** in-body errors (rated table empty / vendor errors). */
  errors?: string[];
  /** in-body warnings. */
  warnings?: string[];
  /** mfg notes (rated_notes). */
  notes?: Array<{ note: string } | string>;
  /** message shown when there are no rows AND no explicit errors (the legacy
   * "Unable to find a … that satisfies all inputted parameters." fallback). */
  emptyMessage?: string;
  rowKey?: (row: R, index: number) => string | number;
  rowClass?: (row: R, index: number) => string | undefined;
  /** body-row click — see RatedSortableTableProps.onRowClick (display index). */
  onRowClick?: (row: R, index: number) => void;
  disableSort?: boolean;
}

export function RatedOutputsTable<R extends Record<string, unknown>>({
  columns,
  rows,
  ratedDatetime,
  errors,
  warnings,
  notes,
  emptyMessage,
  rowKey,
  rowClass,
  onRowClick,
  disableSort,
}: RatedOutputsTableProps<R>) {
  const hasErrors = (errors?.filter(Boolean).length ?? 0) > 0;
  const showEmpty = !rows.length && !hasErrors && emptyMessage;

  return (
    <div className="rated-outputs-table output-group rating">
      <RatedMessageBlock errors={errors} warnings={warnings} />
      {showEmpty ? (
        <div className="error-message">{emptyMessage}</div>
      ) : (
        <RatedSortableTable
          columns={columns}
          rows={rows}
          rowKey={rowKey}
          rowClass={rowClass}
          onRowClick={onRowClick}
          disableSort={disableSort}
        />
      )}
      {notes && notes.length > 0 && <RatedNotesBlock notes={notes} />}
      <RatedOn datetime={ratedDatetime} />
    </div>
  );
}

export default RatedOutputsTable;
